/**
 * History Action Atoms - Undo/Redo and history management
 *
 * These atoms handle history snapshots and undo/redo operations.
 * They work with the history state defined in core/history.ts
 */

import { atom } from "jotai";
import { _workflowsAtom, _workflowsInDBAtom } from "../core/workflows";
import { _phasesAtom, _phasesInDBAtom } from "../core/phases";
import { workflowHistoryAtom, type WorkflowSnapshot } from "../core/history";
import { workflowsAreEqual, phasesAreEqual } from "../persistence/auto-save";
import type { Phase } from "@/isomorphic/schemas";

// ============================================================================
// HISTORY MANAGEMENT
// ============================================================================

/**
 * Add current state to history
 * Called by action atoms after user modifications
 */
export const addToHistoryAtom = atom(null, (get, set) => {
  const workflows = get(_workflowsAtom);
  const allPhases = get(_phasesAtom);

  // Get current workflow (only one per agent)
  const currentWorkflow = workflows[0];
  if (!currentWorkflow) {
    console.warn("[History] No workflow to add to history");
    return;
  }

  const workflowId = currentWorkflow.id;
  const history = get(workflowHistoryAtom(workflowId));

  // Only include phases for this workflow
  const workflowPhases = allPhases.filter((p) => p.workflowId === workflowId);

  const newSnapshot = {
    workflow: currentWorkflow,
    phases: workflowPhases,
    timestamp: Date.now(),
  };

  // Remove future states (everything after currentIndex) and add new state
  let newStates = [...history.states.slice(0, history.currentIndex + 1), newSnapshot];

  // Limit to 20 states (keep the 20 most recent)
  if (newStates.length > 20) {
    newStates = newStates.slice(-20);
  }

  const newIndex = newStates.length - 1;

  set(workflowHistoryAtom(workflowId), {
    states: newStates,
    currentIndex: newIndex,
  });

  console.log(
    `[History] Snapshot added: state ${newIndex + 1}/${newStates.length}, phases:`,
    workflowPhases.map((p) => p.id).join(", "),
  );
});

/**
 * Initialize history with current state (only if history is empty)
 * Called once when workflow is loaded
 *
 * If history exists (after page refresh):
 * - Keep past states (up to currentIndex)
 * - Discard future states (after currentIndex)
 * - Add new state with freshly loaded data from DB
 * - This allows undo to past states, but redo is lost (expected after refresh)
 */
export const initializeHistoryAtom = atom(null, (get, set) => {
  const workflows = get(_workflowsAtom);
  const allPhases = get(_phasesAtom);

  const currentWorkflow = workflows[0];
  if (!currentWorkflow) {
    console.warn("[History] No workflow to initialize history");
    return;
  }

  const workflowId = currentWorkflow.id;
  const history = get(workflowHistoryAtom(workflowId));
  const workflowPhases = allPhases.filter((p) => p.workflowId === workflowId);

  const newSnapshot = {
    workflow: currentWorkflow,
    phases: workflowPhases,
    timestamp: Date.now(),
  };

  // If history exists (page refresh), keep past + add new state with DB data
  if (history.currentIndex !== -1) {
    console.log("[History] History exists after refresh, checking if DB state differs from current state");

    const currentState = history.states[history.currentIndex];

    // Compare workflows (business data only, ignore metadata)
    const workflowEqual = workflowsAreEqual(currentState.workflow, newSnapshot.workflow);

    // Compare phases (business data only, ignore metadata)
    // Must have same count and all phases must match
    const phasesEqual =
      currentState.phases.length === newSnapshot.phases.length &&
      currentState.phases.every((currentPhase) => {
        const matchingPhase = newSnapshot.phases.find((p) => p.id === currentPhase.id);
        return matchingPhase && phasesAreEqual(currentPhase, matchingPhase);
      });

    const statesAreEqual = workflowEqual && phasesEqual;

    console.log("[History] Comparing states:");
    console.log("  Current phases:", currentState.phases.map((p) => p.id).join(", "));
    console.log("  Loaded phases:", newSnapshot.phases.map((p) => p.id).join(", "));
    console.log("  Workflow equal:", workflowEqual);
    console.log("  Phases equal:", phasesEqual);
    console.log("  States equal:", statesAreEqual);

    if (statesAreEqual) {
      // DB state is identical to current state, no need to add duplicate
      console.log("[History] DB state is identical to current state, keeping history as-is");
      return;
    }

    // DB state differs, keep past + add new state
    console.log("[History] DB state differs, adding new state with DB data");
    const newStates = [...history.states.slice(0, history.currentIndex + 1), newSnapshot];

    // Limit to 20 states
    const limitedStates = newStates.length > 20 ? newStates.slice(-20) : newStates;

    set(workflowHistoryAtom(workflowId), {
      states: limitedStates,
      currentIndex: limitedStates.length - 1,
    });

    console.log(
      `[History] History updated after refresh: ${limitedStates.length} states, currentIndex: ${limitedStates.length - 1}`,
    );
    return;
  }

  // History is empty, create initial snapshot
  set(workflowHistoryAtom(workflowId), {
    states: [newSnapshot],
    currentIndex: 0,
  });

  console.log("[History] History initialized for workflow:", workflowId);
});

// ============================================================================
// UNDO/REDO
// ============================================================================

/**
 * Undo - Restore previous snapshot
 */
export const undoAtom = atom(null, (get, set, workflowId: string) => {
  const history = get(workflowHistoryAtom(workflowId));

  if (history.currentIndex <= 0) {
    console.log("[History] Nothing to undo");
    return;
  }

  // Move to previous state
  const newIndex = history.currentIndex - 1;
  const previousState = history.states[newIndex];

  // Restore state
  set(_workflowsAtom, [previousState.workflow]);
  set(_phasesAtom, previousState.phases);

  // Update current index
  set(workflowHistoryAtom(workflowId), {
    states: history.states,
    currentIndex: newIndex,
  });

  console.log(
    `[History] Undo - state ${newIndex + 1}/${history.states.length}:`,
    new Date(previousState.timestamp).toLocaleTimeString(),
  );
});

/**
 * Redo - Restore next snapshot
 */
export const redoAtom = atom(null, (get, set, workflowId: string) => {
  const history = get(workflowHistoryAtom(workflowId));

  if (history.currentIndex >= history.states.length - 1) {
    console.log("[History] Nothing to redo");
    return;
  }

  // Move to next state
  const newIndex = history.currentIndex + 1;
  const nextState = history.states[newIndex];

  // Restore state
  set(_workflowsAtom, [nextState.workflow]);
  set(_phasesAtom, nextState.phases);

  // Update current index
  set(workflowHistoryAtom(workflowId), {
    states: history.states,
    currentIndex: newIndex,
  });

  console.log(
    `[History] Redo - state ${newIndex + 1}/${history.states.length}:`,
    new Date(nextState.timestamp).toLocaleTimeString(),
  );
});

/**
 * Clear history - Debug tool (development only)
 * Completely clears the history for a workflow
 */
export const clearHistoryAtom = atom(null, (get, set, workflowId: string) => {
  // Reset history to empty state
  set(workflowHistoryAtom(workflowId), {
    states: [],
    currentIndex: -1,
  });

  console.log("[History] History cleared for workflow:", workflowId);
});

// ============================================================================
// HISTORY SYNCHRONIZATION (after API operations)
// ============================================================================

/**
 * Update phase ID in all history snapshots (temp ID → UUID after CREATE)
 * Searches by phaseKey because temp ID might have changed
 */
export const updatePhaseIdByPhaseKeyInHistoryAtom = atom(
  null,
  (get, set, workflowId: string, phaseKey: string, newUuid: string) => {
    const history = get(workflowHistoryAtom(workflowId));

    const updateSnapshot = (snapshot: WorkflowSnapshot): WorkflowSnapshot => {
      return {
        ...snapshot,
        phases: snapshot.phases.map((p: Phase) => (p.phaseKey === phaseKey ? { ...p, id: newUuid } : p)),
      };
    };

    set(workflowHistoryAtom(workflowId), {
      states: history.states.map(updateSnapshot),
      currentIndex: history.currentIndex,
    });

    console.log(
      `[History] Updated phase ID in ${history.states.length} states: ${phaseKey} → ${newUuid}, currentIndex: ${history.currentIndex}/${history.states.length - 1}`,
    );
  },
);

/**
 * Update phase ID in all history snapshots (UUID → temp ID after DELETE)
 * Searches by ID to replace deleted UUID with new temp ID
 */
export const updatePhaseIdByIdInHistoryAtom = atom(
  null,
  (get, set, workflowId: string, oldUuid: string, newTempId: string) => {
    const history = get(workflowHistoryAtom(workflowId));

    const updateSnapshot = (snapshot: WorkflowSnapshot): WorkflowSnapshot => {
      return {
        ...snapshot,
        phases: snapshot.phases.map((p: Phase) => (p.id === oldUuid ? { ...p, id: newTempId } : p)),
      };
    };

    set(workflowHistoryAtom(workflowId), {
      states: history.states.map(updateSnapshot),
      currentIndex: history.currentIndex,
    });

    console.log("[History] Updated phase ID in history:", oldUuid, "→", newTempId);
  },
);

/**
 * Update workflow metadata in all history snapshots (after UPDATE)
 * Updates historyVersion, updatedAt, etc. from server response
 */
export const updateWorkflowMetadataInHistoryAtom = atom(
  null,
  (get, set, workflowId: string, metadata: Partial<WorkflowSnapshot["workflow"]>) => {
    const history = get(workflowHistoryAtom(workflowId));

    const updateSnapshot = (snapshot: WorkflowSnapshot): WorkflowSnapshot => {
      return {
        ...snapshot,
        workflow: { ...snapshot.workflow, ...metadata },
      };
    };

    set(workflowHistoryAtom(workflowId), {
      states: history.states.map(updateSnapshot),
      currentIndex: history.currentIndex,
    });

    console.log("[History] Updated workflow metadata in history:", workflowId, metadata);
  },
);

/**
 * Update phase metadata in all history snapshots (after UPDATE)
 * Updates historyVersion, updatedAt, etc. from server response
 */
export const updatePhaseMetadataInHistoryAtom = atom(
  null,
  (get, set, workflowId: string, phaseId: string, metadata: Partial<Phase>) => {
    const history = get(workflowHistoryAtom(workflowId));

    const updateSnapshot = (snapshot: WorkflowSnapshot): WorkflowSnapshot => {
      return {
        ...snapshot,
        phases: snapshot.phases.map((p: Phase) => (p.id === phaseId ? { ...p, ...metadata } : p)),
      };
    };

    set(workflowHistoryAtom(workflowId), {
      states: history.states.map(updateSnapshot),
      currentIndex: history.currentIndex,
    });

    console.log("[History] Updated phase metadata in history:", phaseId, metadata);
  },
);

/**
 * Clear history for a workflow (when leaving editor)
 */
export const clearHistoryForWorkflowAtom = atom(null, (get, set, workflowId: string) => {
  set(workflowHistoryAtom(workflowId), {
    states: [],
    currentIndex: -1,
  });

  console.log("[History] Cleared history for workflow:", workflowId);
});
